import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  Brain,
  Building2,
  CheckCircle2,
  Landmark,
  LockKeyhole,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Wallet
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import {
  getCurrentIdentity,
  resendSignUpEmail,
  signInWithEmail,
  signOutCloud,
  type CloudIdentity
} from "@/integrations/cloud/authService";
import { fetchEntitlementTier } from "@/integrations/cloud/entitlementService";
import { isCloudConfigured } from "@/integrations/cloud/cloudConfig";
import { formatCurrency } from "@/utils/format";
import { calculatePastPerformance } from "@/features/performance/pastPerformance";

const appStoreUrl =
  (import.meta.env.VITE_APPLE_APP_STORE_URL as string | undefined)?.trim() ||
  "https://apps.apple.com/";
const playStoreUrl =
  (import.meta.env.VITE_GOOGLE_PLAY_STORE_URL as string | undefined)?.trim() ||
  "https://play.google.com/store";

const sectionMotion = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 }
};

const clamped = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const stepPrecision = (step: number) => {
  const stepText = step.toString();
  if (!stepText.includes(".")) return 0;
  return stepText.split(".")[1]?.length ?? 0;
};

const normalizeToStep = (value: number, step: number) => {
  const precision = stepPrecision(step);
  const snapped = Math.round(value / step) * step;
  return Number(snapped.toFixed(precision));
};

const parseAndClampNumber = (
  rawValue: string,
  min: number,
  max: number,
  step: number
) => {
  if (rawValue.trim().length === 0) return null;
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return null;
  return normalizeToStep(clamped(parsed, min, max), step);
};

const simulateRemainingLoanBalance = (
  principal: number,
  annualRatePercent: number,
  monthlyRepayment: number,
  months: number
) => {
  let balance = Math.max(0, principal);
  const monthlyRate = annualRatePercent / 1200;

  for (let month = 0; month < months && balance > 0; month += 1) {
    const interest = balance * monthlyRate;
    balance = Math.max(0, balance + interest - monthlyRepayment);
  }

  return balance;
};

const AppleLogoIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    fill="currentColor"
  >
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
);

const AndroidLogoIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    fill="currentColor"
  >
    <path d="M18.4395 5.5586c-.675 1.1664-1.352 2.3318-2.0274 3.498-.0366-.0155-.0742-.0286-.1113-.043-1.8249-.6957-3.484-.8-4.42-.787-1.8551.0185-3.3544.4643-4.2597.8203-.084-.1494-1.7526-3.021-2.0215-3.4864a1.1451 1.1451 0 0 0-.1406-.1914c-.3312-.364-.9054-.4859-1.379-.203-.475.282-.7136.9361-.3886 1.5019 1.9466 3.3696-.0966-.2158 1.9473 3.3593.0172.031-.4946.2642-1.3926 1.0177C2.8987 12.176.452 14.772 0 18.9902h24c-.119-1.1108-.3686-2.099-.7461-3.0683-.7438-1.9118-1.8435-3.2928-2.7402-4.1836a12.1048 12.1048 0 0 0-2.1309-1.6875c.6594-1.122 1.312-2.2559 1.9649-3.3848.2077-.3615.1886-.7956-.0079-1.1191a1.1001 1.1001 0 0 0-.8515-.5332c-.5225-.0536-.9392.3128-1.0488.5449zm-.0391 8.461c.3944.5926.324 1.3306-.1563 1.6503-.4799.3197-1.188.0985-1.582-.4941-.3944-.5927-.324-1.3307.1563-1.6504.4727-.315 1.1812-.1086 1.582.4941zM7.207 13.5273c.4803.3197.5506 1.0577.1563 1.6504-.394.5926-1.1038.8138-1.584.4941-.48-.3197-.5503-1.0577-.1563-1.6504.4008-.6021 1.1087-.8106 1.584-.4941z" />
  </svg>
);

interface LabControlProps {
  label: string;
  hint: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  prefix?: string;
  suffix?: string;
  disabled?: boolean;
}

const LabControl = ({
  label,
  hint,
  value,
  min,
  max,
  step,
  onChange,
  prefix,
  suffix,
  disabled
}: LabControlProps) => {
  const [draftValue, setDraftValue] = useState<string>(value.toString());

  useEffect(() => {
    setDraftValue(value.toString());
  }, [value]);

  const commitDraftValue = (rawValue: string) => {
    const nextValue = parseAndClampNumber(rawValue, min, max, step);
    if (nextValue === null) {
      setDraftValue(value.toString());
      return;
    }
    setDraftValue(nextValue.toString());
    onChange(nextValue);
  };

  return (
    <label className="space-y-1.5 text-xs text-slate-200">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-slate-100">{label}</span>
        <span className="flex items-center gap-1 rounded-md border border-white/20 bg-[#152d57]/80 px-2 py-1">
          {prefix ? <span className="text-[11px] text-slate-300">{prefix}</span> : null}
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            inputMode="decimal"
            value={draftValue}
            onChange={(event) => setDraftValue(event.target.value)}
            onBlur={() => commitDraftValue(draftValue)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                commitDraftValue(draftValue);
              }
            }}
            disabled={disabled}
            className="no-spinner w-20 bg-transparent text-right text-xs text-white outline-none disabled:cursor-not-allowed disabled:text-slate-400"
          />
          {suffix ? <span className="text-[11px] text-slate-300">{suffix}</span> : null}
        </span>
      </div>
      <p className="text-[11px] text-slate-300">{hint}</p>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        disabled={disabled}
        className="w-full accent-cyan-300 disabled:cursor-not-allowed"
      />
    </label>
  );
};

export const WebLandingApp = () => {
  const [identity, setIdentity] = useState<CloudIdentity | null>(null);
  const [tier, setTier] = useState<"FREE" | "PRO">("FREE");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [litePurchaseDate, setLitePurchaseDate] = useState("2020-06-01");
  const [litePurchasePrice, setLitePurchasePrice] = useState(740_000);
  const [liteInitialCashInvested, setLiteInitialCashInvested] = useState(185_000);
  const [liteCurrentValue, setLiteCurrentValue] = useState(1_020_000);
  const [liteCurrentLoanBalance, setLiteCurrentLoanBalance] = useState(560_000);
  const [liteInterestRate, setLiteInterestRate] = useState(6.1);
  const [liteMonthlyRepayment, setLiteMonthlyRepayment] = useState(4_050);
  const [liteWeeklyRent, setLiteWeeklyRent] = useState(760);
  const [liteAnnualCosts, setLiteAnnualCosts] = useState(13_200);
  const [liteVacancyRate, setLiteVacancyRate] = useState(3.8);
  const [capitalGrowthRate, setCapitalGrowthRate] = useState(4.9);
  const [rentGrowthRate, setRentGrowthRate] = useState(3.5);
  const [projectionYears, setProjectionYears] = useState(5);

  useEffect(() => {
    if (!isCloudConfigured) return;
    void (async () => {
      const current = await getCurrentIdentity().catch(() => null);
      if (!current) return;
      setIdentity(current);
      const entitlementTier = await fetchEntitlementTier(current).catch(
        () => "FREE" as const
      );
      setTier(entitlementTier);
    })();
  }, []);

  const pastPreview = useMemo(
    () =>
      calculatePastPerformance({
        purchasePrice: litePurchasePrice,
        purchaseDate: litePurchaseDate,
        currentValue: liteCurrentValue,
        currentLoanBalance: liteCurrentLoanBalance,
        currentInterestRate: liteInterestRate,
        weeklyRent: liteWeeklyRent,
        annualNonDebtCosts: liteAnnualCosts,
        vacancyRate: liteVacancyRate,
        monthlyRepayment: liteMonthlyRepayment,
        initialCashInvested: liteInitialCashInvested,
        benchmarkGrowthRate: 5
      }),
    [
      liteAnnualCosts,
      liteCurrentLoanBalance,
      liteCurrentValue,
      liteInitialCashInvested,
      liteInterestRate,
      liteMonthlyRepayment,
      litePurchaseDate,
      litePurchasePrice,
      liteVacancyRate,
      liteWeeklyRent
    ]
  );

  const forwardPreview = useMemo(() => {
    const projectedValue =
      liteCurrentValue * Math.pow(1 + capitalGrowthRate / 100, projectionYears);
    const projectedLoanBalance = simulateRemainingLoanBalance(
      liteCurrentLoanBalance,
      liteInterestRate,
      liteMonthlyRepayment,
      projectionYears * 12
    );
    const projectedWeeklyRent =
      liteWeeklyRent * Math.pow(1 + rentGrowthRate / 100, projectionYears);
    const projectedEffectiveRent =
      projectedWeeklyRent * 52 * clamped(1 - liteVacancyRate / 100, 0, 1);
    const projectedAnnualCosts =
      liteAnnualCosts * Math.pow(1 + 2.8 / 100, projectionYears);
    const projectedAnnualNet =
      projectedEffectiveRent - projectedAnnualCosts - liteMonthlyRepayment * 12;

    const stressInterestRate = clamped(liteInterestRate + 1.4, 0.5, 14);
    const stressVacancyRate = clamped(liteVacancyRate + 2.4, 0, 20);
    const stressedRepayment = liteMonthlyRepayment * 1.1;
    const stressedRent =
      projectedWeeklyRent * 52 * clamped(1 - stressVacancyRate / 100, 0, 1);
    const stressedNet = stressedRent - projectedAnnualCosts * 1.06 - stressedRepayment * 12;

    return {
      projectedValue,
      projectedLoanBalance,
      projectedEquity: projectedValue - projectedLoanBalance,
      projectedAnnualNet,
      stressInterestRate,
      stressVacancyRate,
      stressedNet
    };
  }, [
    capitalGrowthRate,
    liteAnnualCosts,
    liteCurrentLoanBalance,
    liteCurrentValue,
    liteInterestRate,
    liteMonthlyRepayment,
    liteVacancyRate,
    liteWeeklyRent,
    projectionYears,
    rentGrowthRate
  ]);

  const scoreTone =
    pastPreview.performanceScore >= 72
      ? "text-emerald-300"
      : pastPreview.performanceScore >= 48
        ? "text-amber-200"
        : "text-rose-300";
  const annualNetTone =
    pastPreview.annualNetCashflow >= 0 ? "text-emerald-300" : "text-rose-300";

  return (
    <div className="relative min-h-full overflow-x-hidden bg-[#040a1b] text-[#edf3ff]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 top-[-8rem] h-[30rem] w-[30rem] rounded-full bg-cyan-400/25 blur-3xl" />
        <div className="absolute right-[-10rem] top-24 h-[26rem] w-[26rem] rounded-full bg-blue-500/20 blur-3xl" />
        <div className="absolute bottom-[-14rem] left-1/2 h-[28rem] w-[44rem] -translate-x-1/2 rounded-full bg-emerald-400/15 blur-3xl" />
      </div>

      <div className="relative mx-auto w-full max-w-[1700px] px-4 pb-20 pt-6 sm:px-6 xl:px-10">
        <motion.header
          initial="hidden"
          animate="show"
          variants={sectionMotion}
          transition={{ duration: 0.35 }}
          className="rounded-2xl border border-white/15 bg-[#0c1b3c]/85 p-4 backdrop-blur-md xl:p-5"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-gradient-to-br from-cyan-300 to-blue-500 p-2.5">
                <Building2 size={18} className="text-[#041124]" />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-100">
                  Investment Property Mate
                </p>
                <p className="text-sm text-slate-200">
                  Portfolio intelligence built for Australian property investors
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                className="border-white/20 bg-white/10 text-white hover:bg-white/20"
                onClick={() => window.open(appStoreUrl, "_blank", "noopener,noreferrer")}
              >
                <AppleLogoIcon className="mr-1.5 size-4" />
                App Store
              </Button>
              <Button
                variant="secondary"
                className="border-white/20 bg-white/10 text-white hover:bg-white/20"
                onClick={() => window.open(playStoreUrl, "_blank", "noopener,noreferrer")}
              >
                <AndroidLogoIcon className="mr-1.5 size-4" />
                Google Play
              </Button>
            </div>
          </div>
        </motion.header>

        <motion.section
          initial="hidden"
          animate="show"
          variants={sectionMotion}
          transition={{ delay: 0.04, duration: 0.4 }}
          className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-12"
        >
          <Card className="col-span-12 space-y-5 border-white/15 bg-gradient-to-br from-[#0b1d43]/95 via-[#0e2450]/95 to-[#153167]/95 p-6 shadow-2xl xl:col-span-7 xl:p-8 2xl:p-10">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-cyan-100">
              <Sparkles size={14} />
              Know how your property has actually performed
            </div>
            <h1 className="max-w-4xl text-4xl font-semibold leading-tight tracking-tight md:text-5xl xl:text-6xl 2xl:text-[4.25rem]">
              Start with the truth: your past performance.
            </h1>
            <p className="max-w-3xl text-sm leading-relaxed text-slate-100 md:text-base">
              Investment Property Mate is built around historical review first. Understand
              capital growth, equity creation, and holding quality from purchase to today,
              then layer in forward scenarios once the baseline performance is clear.
            </p>
            <div className="grid gap-2 text-sm text-slate-100 sm:grid-cols-2">
              <p className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-cyan-300" />
                Historical performance scoring by property
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-cyan-300" />
                Capital gain, equity, DSCR and holding analysis
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-cyan-300" />
                Scenario stress-testing when you are ready
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-cyan-300" />
                Local-first privacy with optional account sync
              </p>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                className="bg-gradient-to-r from-cyan-300 to-blue-400 text-[#031223] hover:brightness-105"
                onClick={() => window.open(appStoreUrl, "_blank", "noopener,noreferrer")}
              >
                <AppleLogoIcon className="mr-1.5 size-4" />
                Download on iOS
                <ArrowRight size={16} className="ml-1.5" />
              </Button>
              <Button
                variant="secondary"
                className="border-white/20 bg-white/10 text-white hover:bg-white/20"
                onClick={() => window.open(playStoreUrl, "_blank", "noopener,noreferrer")}
              >
                <AndroidLogoIcon className="mr-1.5 size-4" />
                Download on Android
              </Button>
            </div>
          </Card>

          <Card className="col-span-12 border-white/15 bg-[#0a1735]/90 p-3 xl:col-span-5 xl:p-4">
            <p className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100">
              <BarChart3 size={14} />
              Dashboard preview
            </p>
            <img
              src="/help/screens/dashboard.png"
              alt="Investment Property Mate dashboard with portfolio metrics and projection charts"
              className="aspect-[3/2] w-full rounded-xl border border-white/10 object-cover object-top"
            />
          </Card>
        </motion.section>

        <motion.section
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={sectionMotion}
          transition={{ duration: 0.4 }}
          className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-4"
        >
          {[
            {
              title: "Past Performance Engine",
              body: "Score each property on what has already happened: growth quality, leverage progress, and holding resilience.",
              icon: <Landmark size={18} className="text-cyan-200" />
            },
            {
              title: "Debt & Cashflow Clarity",
              body: "See whether rent and costs are genuinely covering debt service or quietly eroding portfolio quality.",
              icon: <TrendingUp size={18} className="text-cyan-200" />
            },
            {
              title: "Forward Scenario Layer",
              body: "Once baseline performance is clear, test vacancy, rate and growth assumptions before your next move.",
              icon: <Brain size={18} className="text-cyan-200" />
            },
            {
              title: "Security & Continuity",
              body: "Entitlements are verified server-side and linked to your account so access follows you across devices.",
              icon: <LockKeyhole size={18} className="text-cyan-200" />
            }
          ].map((item) => (
            <Card key={item.title} className="border-white/15 bg-[#0f2247]/92 p-4">
              <p className="mb-2 inline-flex items-center gap-2 text-sm font-semibold text-slate-100">
                {item.icon}
                {item.title}
              </p>
              <p className="text-sm leading-relaxed text-slate-200">{item.body}</p>
            </Card>
          ))}
        </motion.section>

        <motion.section
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-70px" }}
          variants={sectionMotion}
          transition={{ duration: 0.45 }}
          className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-12"
        >
          <Card className="col-span-12 space-y-4 border-white/15 bg-[#0c1f43]/92 p-5 xl:col-span-7 xl:p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-2xl font-semibold">Past Performance Lite</h2>
              <p className="inline-flex items-center gap-1 rounded-full border border-emerald-300/35 bg-emerald-300/10 px-2.5 py-1 text-xs font-semibold text-emerald-200">
                <Brain size={14} />
                Free mini analysis
              </p>
            </div>
            <p className="text-sm text-slate-200">
              Main focus: how your property has performed from purchase to today.
              Enter known values and get an immediate performance score, holding quality
              check, and practical insights.
            </p>
            <div className="rounded-xl border border-emerald-200/50 bg-gradient-to-r from-emerald-300/20 to-cyan-300/20 p-3">
              <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-emerald-100">
                <ShieldCheck size={14} />
                Privacy-first calculator
              </p>
              <p className="mt-1 text-sm text-slate-100">
                Your inputs stay private. All calculations in this lite tool run locally
                in your browser and are not stored on our servers.
              </p>
              <a
                href="/privacy"
                className="mt-2 inline-block text-xs font-semibold text-cyan-100 underline decoration-cyan-200/70 underline-offset-2 hover:text-white"
              >
                Read Privacy Policy
              </a>
            </div>

            <div className="rounded-xl border border-white/15 bg-[#112a56]/70 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-cyan-100">
                Acquisition baseline
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="space-y-1.5 text-xs text-slate-200">
                  <span className="font-medium text-slate-100">Purchase date</span>
                  <p className="text-[11px] text-slate-300">
                    Used to calculate years held and annualised performance.
                  </p>
                  <input
                    type="date"
                    value={litePurchaseDate}
                    onChange={(event) => setLitePurchaseDate(event.target.value)}
                    className="w-full rounded-md border border-white/20 bg-[#152d57]/80 px-2 py-2 text-xs text-white"
                  />
                </label>
                <LabControl
                  label="Purchase price"
                  hint="Original purchase contract price."
                  value={litePurchasePrice}
                  min={150_000}
                  max={5_000_000}
                  step={5_000}
                  prefix="$"
                  onChange={setLitePurchasePrice}
                />
                <LabControl
                  label="Initial cash invested"
                  hint="Deposit + stamp duty + buying/reno costs."
                  value={liteInitialCashInvested}
                  min={20_000}
                  max={2_500_000}
                  step={1_000}
                  prefix="$"
                  onChange={setLiteInitialCashInvested}
                />
              </div>
            </div>

            <div className="rounded-xl border border-white/15 bg-[#112a56]/70 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-cyan-100">
                Current position
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <LabControl
                  label="Approx current value"
                  hint="Current best estimate of market value."
                  value={liteCurrentValue}
                  min={150_000}
                  max={6_000_000}
                  step={5_000}
                  prefix="$"
                  onChange={setLiteCurrentValue}
                />
                <LabControl
                  label="Current loan balance"
                  hint="Amount still owing today."
                  value={liteCurrentLoanBalance}
                  min={0}
                  max={4_500_000}
                  step={5_000}
                  prefix="$"
                  onChange={setLiteCurrentLoanBalance}
                />
                <LabControl
                  label="Current mortgage rate"
                  hint="Blended rate across your debt facilities."
                  value={liteInterestRate}
                  min={1}
                  max={14}
                  step={0.05}
                  suffix="%"
                  onChange={setLiteInterestRate}
                />
                <LabControl
                  label="Monthly mortgage repayment"
                  hint="Total monthly repayment for this property."
                  value={liteMonthlyRepayment}
                  min={0}
                  max={25_000}
                  step={25}
                  prefix="$"
                  onChange={setLiteMonthlyRepayment}
                />
                <LabControl
                  label="Weekly rent"
                  hint="Current expected market rent."
                  value={liteWeeklyRent}
                  min={100}
                  max={3_000}
                  step={10}
                  prefix="$"
                  onChange={setLiteWeeklyRent}
                />
                <LabControl
                  label="Annual non-debt costs"
                  hint="Rates, insurance, maintenance, management, misc."
                  value={liteAnnualCosts}
                  min={0}
                  max={80_000}
                  step={100}
                  prefix="$"
                  onChange={setLiteAnnualCosts}
                />
                <LabControl
                  label="Vacancy allowance"
                  hint="Expected % of rent lost to vacancy each year."
                  value={liteVacancyRate}
                  min={0}
                  max={18}
                  step={0.1}
                  suffix="%"
                  onChange={setLiteVacancyRate}
                />
              </div>
            </div>

            <div className="rounded-xl border border-white/15 bg-[#112a56]/70 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-cyan-100">
                Optional forward extension
              </p>
              <p className="mb-2 text-xs text-slate-300">
                Keep past analysis as primary. Add a short forward view only as a secondary check.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <LabControl
                  label="Capital growth assumption"
                  hint="Used only for optional forward estimate."
                  value={capitalGrowthRate}
                  min={-3}
                  max={12}
                  step={0.1}
                  suffix="%"
                  onChange={setCapitalGrowthRate}
                />
                <LabControl
                  label="Rent growth assumption"
                  hint="Used only for optional forward estimate."
                  value={rentGrowthRate}
                  min={-2}
                  max={10}
                  step={0.1}
                  suffix="%"
                  onChange={setRentGrowthRate}
                />
                <LabControl
                  label="Forward horizon"
                  hint="Years for optional forward estimate."
                  value={projectionYears}
                  min={2}
                  max={15}
                  step={1}
                  suffix="yrs"
                  onChange={setProjectionYears}
                />
              </div>
            </div>
          </Card>

          <Card className="col-span-12 space-y-3 border-white/15 bg-gradient-to-br from-[#0d2247]/90 to-[#0a1733]/90 p-5 xl:col-span-5 xl:p-6">
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100">
              <Wallet size={14} />
              Past performance output
            </p>

            <div className="rounded-xl border border-white/15 bg-[#152d57]/85 p-3">
              <p className="text-xs text-slate-200">Performance score</p>
              <p className={`text-3xl font-semibold ${scoreTone}`}>
                {pastPreview.performanceScore}/100
              </p>
              <p className="text-xs text-slate-300">
                {pastPreview.scoreBand} • Held {pastPreview.yearsHeld.toFixed(1)} years
              </p>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
              <div className="rounded-xl border border-white/15 bg-[#152d57]/85 p-3">
                <p className="text-xs text-slate-200">Capital performance</p>
                <p className="text-xl font-semibold">{formatCurrency(pastPreview.capitalGain)}</p>
                <p className="text-xs text-slate-300">
                  Growth: {pastPreview.capitalGrowthPercent.toFixed(1)}% • CAGR{" "}
                  {pastPreview.capitalCagrPercent.toFixed(2)}%
                </p>
              </div>

              <div className="rounded-xl border border-white/15 bg-[#152d57]/85 p-3">
                <p className="text-xs text-slate-200">Equity position</p>
                <p className="text-xl font-semibold">{formatCurrency(pastPreview.equityNow)}</p>
                <p className="text-xs text-slate-300">
                  Equity created: {formatCurrency(pastPreview.equityGainSincePurchase)}
                </p>
                <p className="text-xs text-slate-300">
                  Equity multiple: {pastPreview.equityMultiple.toFixed(2)}x
                </p>
              </div>

              <div className="rounded-xl border border-white/15 bg-[#152d57]/85 p-3">
                <p className="text-xs text-slate-200">Holding quality</p>
                <p className={`text-xl font-semibold ${annualNetTone}`}>
                  {formatCurrency(pastPreview.annualNetCashflow)}
                </p>
                <p className="text-xs text-slate-300">
                  DSCR: {pastPreview.debtCoverageRatio.toFixed(2)}x
                </p>
                <p className="text-xs text-slate-300">
                  Break-even rent: {formatCurrency(pastPreview.breakEvenWeeklyRent)} / week
                </p>
                <p className="text-xs text-slate-300">
                  Current LVR: {pastPreview.loanToValueRatio.toFixed(1)}%
                </p>
              </div>

              <div className="rounded-xl border border-white/15 bg-[#152d57]/85 p-3">
                <p className="text-xs text-slate-200">Total return (approx)</p>
                <p className="text-xl font-semibold">
                  {formatCurrency(pastPreview.totalReturnApprox)}
                </p>
                <p className="text-xs text-slate-300">
                  Capital gain + estimated cumulative net holding cashflow.
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-white/15 bg-[#152d57]/85 p-3">
              <p className="text-xs text-slate-200">Insight summary</p>
              <div className="mt-2 space-y-1">
                {pastPreview.insights.map((insight) => (
                  <p key={insight} className="text-xs text-slate-300">
                    {insight}
                  </p>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-cyan-200/25 bg-cyan-200/10 p-3">
              <p className="text-xs text-cyan-100">
                Optional forward check ({projectionYears}y): projected equity{" "}
                {formatCurrency(forwardPreview.projectedEquity)} with stressed annual net{" "}
                {formatCurrency(forwardPreview.stressedNet)} at{" "}
                {forwardPreview.stressInterestRate.toFixed(1)}% rate /{" "}
                {forwardPreview.stressVacancyRate.toFixed(1)}% vacancy.
              </p>
            </div>

            <p className="rounded-xl border border-cyan-200/25 bg-cyan-200/10 p-3 text-xs text-cyan-100">
              Full app version deepens this with per-property history, expense categories,
              loan-structure-aware scoring, and scenario tracking over time.
            </p>
          </Card>
        </motion.section>

        <motion.section
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-70px" }}
          variants={sectionMotion}
          transition={{ duration: 0.45 }}
          className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-12"
        >
          <Card className="col-span-12 space-y-3 border-white/15 bg-[#0f2247]/92 p-5 xl:col-span-5 xl:p-6">
            <h2 className="text-2xl font-semibold">Web Login & Status</h2>
            <p className="text-sm text-slate-200">
              Account creation and subscriptions happen in mobile apps. Use desktop
              login to verify account status and continuity.
            </p>

            {!isCloudConfigured ? (
              <p className="text-sm text-slate-300">
                Web login is not configured. Set `VITE_CLOUD_API_URL`.
              </p>
            ) : null}

            {isCloudConfigured && !identity ? (
              <div className="space-y-3">
                <label className="space-y-1 text-sm">
                  <span className="text-slate-100">Email</span>
                  <input
                    className="w-full rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-[#9db0db]"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-slate-100">Password</span>
                  <input
                    className="w-full rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-[#9db0db]"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Account password"
                  />
                </label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    disabled={busy || email.trim().length === 0 || password.length === 0}
                    onClick={() => {
                      void (async () => {
                        setBusy(true);
                        setMessage(null);
                        try {
                          const signedIn = await signInWithEmail(email.trim(), password);
                          setIdentity(signedIn);
                          const entitlementTier = await fetchEntitlementTier(signedIn);
                          setTier(entitlementTier);
                          setMessage(`Signed in as ${signedIn.email ?? signedIn.id}`);
                        } catch (error) {
                          setMessage(
                            error instanceof Error ? error.message : "Unable to sign in."
                          );
                        } finally {
                          setBusy(false);
                        }
                      })();
                    }}
                  >
                    Sign in
                  </Button>
                  <Button
                    variant="ghost"
                    className="text-cyan-100 hover:bg-white/10"
                    disabled={busy || email.trim().length === 0}
                    onClick={() => {
                      void (async () => {
                        setBusy(true);
                        setMessage(null);
                        try {
                          await resendSignUpEmail(email.trim());
                          setMessage("Verification email sent. Check inbox and spam.");
                        } catch (error) {
                          setMessage(
                            error instanceof Error
                              ? error.message
                              : "Unable to resend verification email."
                          );
                        } finally {
                          setBusy(false);
                        }
                      })();
                    }}
                  >
                    Resend verification email
                  </Button>
                </div>
              </div>
            ) : null}

            {identity ? (
              <div className="space-y-3 rounded-xl border border-white/20 bg-[#152d57]/80 p-3">
                <p className="text-sm font-semibold">
                  Signed in as {identity.email ?? identity.id}
                </p>
                <p className="text-sm text-slate-200">
                  Subscription: {tier === "PRO" ? "PRO active" : "FREE"}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    className="border-white/20 bg-white/10 text-white hover:bg-white/20"
                    onClick={() => {
                      void (async () => {
                        setBusy(true);
                        setMessage(null);
                        try {
                          const entitlementTier = await fetchEntitlementTier(identity);
                          setTier(entitlementTier);
                          setMessage("Account status refreshed.");
                        } catch (error) {
                          setMessage(
                            error instanceof Error
                              ? error.message
                              : "Unable to refresh account status."
                          );
                        } finally {
                          setBusy(false);
                        }
                      })();
                    }}
                    disabled={busy}
                  >
                    <RefreshCw size={15} className="mr-1.5" />
                    Refresh status
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      void (async () => {
                        await signOutCloud();
                        setIdentity(null);
                        setTier("FREE");
                        setMessage("Signed out.");
                      })();
                    }}
                    disabled={busy}
                  >
                    Sign out
                  </Button>
                </div>
              </div>
            ) : null}

            {message ? <p className="text-xs text-slate-200">{message}</p> : null}
          </Card>

          <Card className="col-span-12 space-y-4 border-white/15 bg-[#0b1734]/90 p-5 xl:col-span-7 xl:p-6">
            <h2 className="text-2xl font-semibold">Why serious investors adopt this workflow</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {[
                {
                  icon: <ShieldCheck size={18} className="text-cyan-200" />,
                  title: "Data you control",
                  body: "Local-first design keeps your portfolio under your control, with optional cloud backup when you choose."
                },
                {
                  icon: <Brain size={18} className="text-cyan-200" />,
                  title: "Faster decisions",
                  body: "Scenario modelling gives concrete downside numbers before refinance, renovation, or acquisition decisions."
                },
                {
                  icon: <Wallet size={18} className="text-cyan-200" />,
                  title: "Cashflow truth",
                  body: "Surface operating cost pressure and debt strain early so you can intervene before returns degrade."
                },
                {
                  icon: <LockKeyhole size={18} className="text-cyan-200" />,
                  title: "Cross-device continuity",
                  body: "Subscription entitlement is tied to your account and verified server-side for consistent access."
                }
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-xl border border-white/15 bg-[#152d57]/80 p-3"
                >
                  <p className="mb-1 inline-flex items-center gap-2 text-sm font-semibold">
                    {item.icon}
                    {item.title}
                  </p>
                  <p className="text-sm leading-relaxed text-slate-200">{item.body}</p>
                </div>
              ))}
            </div>
            <div className="rounded-xl border border-cyan-200/25 bg-cyan-200/10 p-3 text-sm text-cyan-100">
              Built in Brisbane for investors who want fewer surprises and stronger
              conviction in every portfolio move.
            </div>
          </Card>
        </motion.section>

        <motion.section
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          variants={sectionMotion}
          transition={{ duration: 0.45 }}
          className="mt-5 rounded-2xl border border-white/15 bg-gradient-to-r from-[#123773]/75 via-[#11336f]/70 to-[#0e2d61]/75 p-6 xl:p-8"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="max-w-4xl space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100">
                Start now
              </p>
              <h2 className="text-2xl font-semibold md:text-3xl">
                Install free and run your first portfolio stress test in under 10 minutes.
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                className="bg-gradient-to-r from-cyan-300 to-blue-400 text-[#031021] hover:brightness-105"
                onClick={() => window.open(appStoreUrl, "_blank", "noopener,noreferrer")}
              >
                <AppleLogoIcon className="mr-1.5 size-4" />
                Get iOS App
                <ArrowRight size={16} className="ml-1.5" />
              </Button>
              <Button
                variant="secondary"
                className="border-white/20 bg-white/10 text-white hover:bg-white/20"
                onClick={() => window.open(playStoreUrl, "_blank", "noopener,noreferrer")}
              >
                <AndroidLogoIcon className="mr-1.5 size-4" />
                Get Android App
              </Button>
            </div>
          </div>
        </motion.section>

        <footer className="mt-6 rounded-2xl border border-white/10 bg-[#08162f]/90 px-5 py-4 text-xs text-slate-300">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p>© 2026 Investment Property Mate. All rights reserved.</p>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href="/privacy"
                className="text-cyan-100 underline decoration-cyan-200/60 underline-offset-2 hover:text-white"
              >
                Privacy Policy
              </a>
              <a
                href="/legal/terms.html"
                className="text-cyan-100 underline decoration-cyan-200/60 underline-offset-2 hover:text-white"
              >
                Terms
              </a>
              <a
                href="mailto:admin@investmentpropertymate.com"
                className="text-cyan-100 underline decoration-cyan-200/60 underline-offset-2 hover:text-white"
              >
                admin@investmentpropertymate.com
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};
